import { createConfigParametersRepository } from '../../configParametersRepository';
import { handleGaGroupIdError } from '../../configParametersRepository/error';
import {
  pushStoredEvent,
  removeStoredEvent,
} from '../thirdPartyTrackingAgent/eventStorage';
import type {
  GtagEvent,
  GtagEventParams,
} from '../thirdPartyTrackingAgent/types';
import { shouldSkipTracking } from '../utils/cookie';

/**
 * Calls Google Analytics 4 `gtag` function with the given `args` for all trackers on the page.
 * If the `shouldSkipTracking()` function returns `true`, no tracking is performed.
 *
 * @param {...any[]} args - The arguments to pass to the `gtag` function.
 */
export const gtagWrapper: Gtag.Gtag = (command, ...args) => {
  const { getGenericParameters } = createConfigParametersRepository();
  const { isNativeInterface } = getGenericParameters();

  if (shouldSkipTracking() || isNativeInterface) {
    return;
  }
  if (command === 'event') {
    const [eventName, customParams] = args as [string, GtagEventParams];
    const send_to = getGa4GroupId();
    const params: GtagEventParams = {
      send_to,
      edo_timestamp: Date.now(), // important to have this before customParams in case it's overwritten
      ...customParams,
    };
    const uuid = pushStoredEvent([command, eventName, params] as GtagEvent);
    window.gtag?.('event', eventName, {
      ...params,
      ...(uuid && {
        event_callback: () => {
          params.event_callback?.();
          removeStoredEvent(uuid);
        },
      }),
    });
    return;
  }
  window.gtag?.(command, ...args);
};

/**
 * Retrieves the Google Analytics 4 group ID from the configuration parameters repository.
 * If the group ID is not found or invalid, an error is handled.
 * @returns {string | undefined} The Google Analytics 4 group ID, or undefined if not found.
 */
export function getGa4GroupId(): string | undefined {
  const { getGoogleAnalyticsParameters } = createConfigParametersRepository();
  const { ga4GroupId } = getGoogleAnalyticsParameters();
  handleGaGroupIdError(ga4GroupId);
  return ga4GroupId;
}

/**
 * This function generates a URL based on the provided page name and optional URL.
 * It parses the URL, extracts and handles UTM parameters, and constructs a new URL.
 *
 * @param {string} pageName - The name of the page to be included in the URL path.
 *
 * @returns {string} The constructed URL including the page name, UTM parameters, and other parameters from the original URL.
 */
export const getPageLocation = (pageName: string): string | undefined => {
  if (!pageName) return;
  const url = new URL(window.location.href);
  const utmParams: Record<string, string> = {};
  const otherUtms: Record<string, string> = {};
  const otherParams: Record<string, string> = {};

  url.searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      utmParams[key] = value;
    } else if (key === 'gclid' || key === 'mktportal') {
      otherUtms[key] = value;
    } else {
      otherParams[key] = value;
    }
  });

  const customPagePath = `${pageName.replace('//', '/')}`;
  const newUrl = new URL(customPagePath, url.origin);

  Object.entries(utmParams).forEach(([key, value]) => {
    newUrl.searchParams.append(key, value);
  });

  Object.entries(otherUtms).forEach(([key, value]) => {
    newUrl.searchParams.append(key, value);
  });

  Object.entries(otherParams).forEach(([key, value]) => {
    newUrl.searchParams.append(key, value);
  });

  if (url.hash) {
    return `${newUrl.toString()}${url.hash}`;
  }

  return newUrl.toString();
};
